import '../../_shared/form_builder_2/scss/index.scss';
import FormAccordion from './form-accordion';
import { scrollToElement } from '../../utils/dom';

export default class FormView {
    constructor(parent, router) {
        this.router = router;
        this.parent = parent;
        this.el = parent.el.querySelector('.form-view');
        this.form = parent.form;
        this.formEl = this.form.form;
        this.formSectionsWrapper = this.el.querySelector('.form_accordion_wrapper');
        this.formSections = [...this.el.querySelectorAll('.form_accordion')];
        this.stepNumberText = this.el.querySelector('.step-number');
        this.progressBarSteps = this.el.querySelectorAll('.progress-bar--steps');
        this.continueButtons = this.el.querySelectorAll('.continue-button');
        this.saveButtons = this.el.querySelectorAll('.cta-button--save-progress');
        this.backLink = this.el.querySelector('#back-to-eligibility');
        this.helpLink = this.el.querySelector('#help-view-link');

        this.bind();
        this.init();

        this.formAccordion = new FormAccordion(this);
    }

    init() {
        if (this.formSections && this.formSections.length) {
            this.formSections[0].querySelector('.accordion_title').classList.add('open');
            this.formSections[0].querySelector('.accordion_content').classList.remove('hide');
            this.formSections[0].classList.remove('disabled');
            this.formSections[0].classList.add('in-progress');
        }
    }

    bind() {
        this.form.addEventListener('onUpdate', async (field) => {
            const section = field.el.closest('.form_accordion');
            if (section) {
                const fields = this.form.elementFields(section);
                const populated = await this.form.populated(fields);
                this.toggleButtons(section, populated);
            }
            this.hideSaveStatus();
        });
        this.continueButtons.forEach((continueBtn) => {
            continueBtn.addEventListener('click', (e) => {
                e.preventDefault();

                this.nextSection(e);
                this.hideSaveStatus();
            });
        });

        this.saveButtons.forEach((saveBtn) => {
            saveBtn.addEventListener('click', async (e) => {
                e.preventDefault();

                this.saveSection(e);
            });
        });

        this.backLink.addEventListener('click', (e) => {
            e.preventDefault();
            this.router.update('eligibility');
        });

        this.helpLink.addEventListener('click', (e) => {
            e.preventDefault();

            this.analyticsPush(e.currentTarget, this.parent.order?.summary?.ids?.modeCode);
            this.router.update('help');
        });
    }

    async saveSection(e) {
        const saveBtn = e.target;
        const thisSection = saveBtn.closest('.form_accordion');
        const successStatus = thisSection.querySelector('.save-progress-success-status');
        const failedStatus = thisSection.querySelector('.save-progress-failed-status');
        const fields = this.form.elementFields(thisSection);
        const valid = await this.form.valid(fields);
        if (valid) {
            this.form.showLoader();
            const success = await HONDA.MyAccount2.client.saveFinanceApplication(this.formEl);
            this.form.hideLoader();
            if (success) {
                failedStatus.classList.add('hide');
                successStatus.classList.remove('hide');
                this.analyticsPush(saveBtn, this.parent.order?.summary?.ids?.modeCode);
            } else {
                successStatus.classList.add('hide');
                failedStatus.classList.remove('hide');
            }
        } else {
            this.scrollToError(thisSection);
        }
    }

    hideSaveStatus() {
        this.el.querySelectorAll('.save-progress-status').forEach((el) => {
            el.classList.add('hide');
        });
    }

    async nextSection(e) {
        const thisSection = e.target.closest('.form_accordion');
        const fields = this.form.elementFields(thisSection);
        const valid = await this.form.valid(fields);

        if (valid) {
            this.el.scrollIntoView({ behavior: 'smooth' });
            this.showNextSection(thisSection);
            this.analyticsPush(e.currentTarget, this.parent.order?.summary?.ids?.modeCode);
        } else {
            this.scrollToError(thisSection);
        }
    }

    toggleButtons(section, populated) {
        const continueBtn = section.querySelector('.continue-button');
        const saveBtn = section.querySelector('.cta-button--save-progress');

        if (populated) {
            continueBtn?.classList?.remove('disabled');
            saveBtn?.classList?.remove('disabled');
        } else {
            continueBtn?.classList?.add('disabled');
            saveBtn?.classList?.add('disabled');
        }
    }

    scrollToError(thisSection) {
        const fieldError = thisSection.querySelector('.form_field_error');
        if (fieldError) { fieldError.scrollIntoView(); }
    }

    showNextSection(thisSection) {
        const accordionTitle = thisSection.querySelector('.accordion_title');
        thisSection.classList.remove('disabled', 'in-progress');
        thisSection.classList.add('completed');
        accordionTitle.classList.toggle('open');
        const index = this.formSections.indexOf(thisSection);
        const nextSection = this.formSections[index + 1];
        if (nextSection) {
            this.hideSections();
            this.showSection(nextSection);
            this.updateProgressBar(index + 1);
        }
    }

    async progressSections() {
        const validPromises = this.formSections.map((section) => {
            const fields = this.form.elementFields(section);
            return this.form.valid(fields, true);
        });

        const valid = await Promise.all(validPromises);

        this.formSections.every((section, index) => {
            if (index < (this.formSections.length - 1) && valid[index]) {
                this.toggleButtons(section, valid[index]);
                this.showNextSection(section);
            }
            return valid[index];
        });
    }

    hideSections() {
        this.formSections.forEach((section) => section.querySelector('.accordion_content').classList.add('hide'));
    }

    showSection(section) {
        section.querySelector('.accordion_content').classList.remove('hide');
        section.classList.remove('disabled');
        section.classList.add('in-progress');
        section.querySelector('.accordion_title').classList.toggle('open');
        this.scrollToSection(section);
    }

    scrollToSection(section) {
        const progressBar = this.el.querySelector('.progress-bar');
        const progressBarDetails = this.el.querySelector('.progress-bar--details');
        const offset = progressBar.clientHeight + progressBarDetails.clientHeight + 10;
        scrollToElement(section, -offset);
    }

    showErrorSections() {
        this.formSections.forEach(async (section) => {
            const fields = this.form.elementFields(section);
            const valid = await this.form.valid(fields);
            if (!valid) {
                this.showSection(section);
                this.scrollToSection(section);
            }
        });
    }

    updateProgressBar(currentStepIndex) {
        this.stepNumberText.innerText = (currentStepIndex + 1).toString();
        this.progressBarSteps.forEach((step, index) => {
            this.progressBarSteps[index].classList.remove('current-step');
            if (index < currentStepIndex) {
                step.classList.add('completed-step');
            } else if (index === currentStepIndex) {
                step.classList.add('current-step');
            }
        });
    }

    show() {
        this.el.classList.remove('hide');
    }

    hide() {
        this.el.classList.add('hide');
    }

    analyticsPush(el, model) {
        this.parent.AnalyticsHelper.template(el, { model });
        this.parent.AnalyticsHelper.push(el);
    }
}
